import { useCallback, useState } from "react";

import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import classNames from "classnames";

import AddIcon from "@/material-icons/400-24px/add.svg?react";
import ArrowBackIcon from "@/material-icons/400-24px/arrow_back.svg?react";
import ChevronLeftIcon from "@/material-icons/400-24px/chevron_left.svg?react";
import ChevronRightIcon from "@/material-icons/400-24px/chevron_right.svg?react";
import CloseIcon from "@/material-icons/400-24px/close.svg?react";
import UnfoldLessIcon from "@/material-icons/400-24px/unfold_less.svg?react";
import UnfoldMoreIcon from "@/material-icons/400-24px/unfold_more.svg?react";
import type { IconProp } from "flavours/glitch/components/icon";
import { Icon } from "flavours/glitch/components/icon";
import { ButtonInTabsBar } from "flavours/glitch/features/ui/util/columns_context";
import { useIdentity } from "flavours/glitch/identity_context";

import { useColumnIndexContext } from "../features/ui/components/columns_area";
import { getColumnSkipLinkId } from "../features/ui/components/skip_links";

import { NavigationFocusTarget } from "./navigation_focus_target";
import { useAppHistory } from "./router";

export const messages = defineMessages({
  show: { id: "column_header.show_settings", defaultMessage: "Show settings" },
  hide: { id: "column_header.hide_settings", defaultMessage: "Hide settings" },
  moveLeft: {
    id: "column_header.moveLeft_settings",
    defaultMessage: "Move column to the left",
  },
  moveRight: {
    id: "column_header.moveRight_settings",
    defaultMessage: "Move column to the right",
  },
  back: { id: "column_back_button.label", defaultMessage: "Back" },
});

const BackButton: React.FC<{
  hasTitle: boolean;
}> = ({ hasTitle }) => {
  const history = useAppHistory();
  const intl = useIntl();
  const columnIndex = useColumnIndexContext();

  const handleBackClick = useCallback(() => {
    if (history.location.state?.fromMastodon) {
      // goBack() relies on the browser's real native session-history stack
      // actually having an earlier entry to return to -- which can go out
      // of sync with our own location.state.fromMastodon flag (e.g. a
      // page reload, such as the "check for updates" button, keeps the
      // reloaded entry's state but resets what's actually behind it in
      // the tab's history). When that happens goBack() silently does
      // nothing: no error, no navigation, no console output -- confirmed
      // live on a real device 2026-09-24. If nothing actually navigates
      // within 300ms, fall back to an explicit destination instead of
      // leaving the user stuck.
      let backSucceeded = false;
      const unlisten = history.listen(() => {
        backSucceeded = true;
        unlisten();
      });

      history.goBack();

      setTimeout(() => {
        if (!backSucceeded) {
          unlisten();
          history.push("/");
        }
      }, 300);
    } else {
      history.push("/");
    }
  }, [history]);

  return (
    <button
      onClick={handleBackClick}
      className={classNames("column-header__back-button", {
        compact: hasTitle,
      })}
      id={!hasTitle ? getColumnSkipLinkId(columnIndex) : undefined}
      aria-label={intl.formatMessage(messages.back)}
      type="button"
    >
      <Icon id="chevron-left" icon={ArrowBackIcon} className="column-back-button__icon" />
      {!hasTitle && <FormattedMessage id="column_back_button.label" defaultMessage="Back" />}
    </button>
  );
};

export interface Props {
  title?: React.ReactNode;
  icon?: string;
  iconComponent?: IconProp;
  active?: boolean;
  children?: React.ReactNode;
  className?: string;
  pinned?: boolean;
  multiColumn?: boolean;
  extraButton?: React.ReactNode;
  showBackButton?: boolean;
  placeholder?: boolean;
  appendContent?: React.ReactNode;
  collapseIssues?: boolean;
  onClick?: () => void;
  onMove?: (arg0: number) => void;
  onPin?: () => void;
}

export const ColumnHeader: React.FC<Props> = ({
  title,
  icon,
  iconComponent,
  active,
  children,
  className,
  pinned,
  multiColumn,
  extraButton,
  showBackButton,
  placeholder,
  appendContent,
  collapseIssues,
  onClick,
  onMove,
  onPin,
}) => {
  const intl = useIntl();
  const { signedIn } = useIdentity();
  const history = useAppHistory();
  const [collapsed, setCollapsed] = useState(true);
  const [animating, setAnimating] = useState(false);

  const handleToggleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsed((value) => !value);
      setAnimating(true);
    },
    [setCollapsed, setAnimating],
  );

  const handleTitleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  const handleMoveLeft = useCallback(() => {
    onMove?.(-1);
  }, [onMove]);

  const handleMoveRight = useCallback(() => {
    onMove?.(1);
  }, [onMove]);

  const handleTransitionEnd = useCallback(() => {
    setAnimating(false);
  }, [setAnimating]);

  const handlePin = useCallback(() => {
    if (!pinned) {
      history.replace("/");
    }

    onPin?.();
  }, [history, pinned, onPin]);

  const wrapperClassName = classNames("column-header__wrapper", className, {
    active,
  });

  const headingClassName = classNames("column-header", {
    active,
  });

  const collapsibleClassName = classNames("column-header__collapsible", {
    collapsed,
    animating,
  });

  const collapsibleButtonClassName = classNames("column-header__button", {
    active: !collapsed,
  });

  let extraContent, pinButton, moveButtons, backButton, collapseButton;

  if (children) {
    extraContent = (
      <div key="extra-content" className="column-header__collapsible__extra">
        {children}
      </div>
    );
  }

  if (multiColumn && pinned) {
    pinButton = (
      <button className="text-btn column-header__setting-btn" onClick={handlePin} type="button">
        <Icon id="times" icon={CloseIcon} />{" "}
        <FormattedMessage id="column_header.unpin" defaultMessage="Unpin" />
      </button>
    );

    moveButtons = (
      <div className="column-header__setting-arrows">
        <button
          title={intl.formatMessage(messages.moveLeft)}
          aria-label={intl.formatMessage(messages.moveLeft)}
          className="icon-button column-header__setting-btn"
          onClick={handleMoveLeft}
          type="button"
        >
          <Icon id="chevron-left" icon={ChevronLeftIcon} />
        </button>
        <button
          title={intl.formatMessage(messages.moveRight)}
          aria-label={intl.formatMessage(messages.moveRight)}
          className="icon-button column-header__setting-btn"
          onClick={handleMoveRight}
          type="button"
        >
          <Icon id="chevron-right" icon={ChevronRightIcon} />
        </button>
      </div>
    );
  } else if (multiColumn && onPin) {
    pinButton = (
      <button className="text-btn column-header__setting-btn" onClick={handlePin} type="button">
        <Icon id="plus" icon={AddIcon} />{" "}
        <FormattedMessage id="column_header.pin" defaultMessage="Pin" />
      </button>
    );
  }

  if (!pinned && ((multiColumn && history.location.state?.fromMastodon) || showBackButton)) {
    backButton = <BackButton hasTitle={!!title} />;
  }

  const collapsedContent = [extraContent];

  if (multiColumn) {
    collapsedContent.push(
      <div key="buttons" className="column-header__advanced-buttons">
        {pinButton}
        {moveButtons}
      </div>,
    );
  }

  if (signedIn && (children || (multiColumn && onPin))) {
    collapseButton = (
      <button
        className={collapsibleButtonClassName}
        title={intl.formatMessage(collapsed ? messages.show : messages.hide)}
        aria-label={intl.formatMessage(collapsed ? messages.show : messages.hide)}
        onClick={handleToggleClick}
        type="button"
      >
        <i className="icon-with-badge">
          <Icon id="sliders" icon={collapsed ? UnfoldMoreIcon : UnfoldLessIcon} />
          {collapseIssues && <i className="icon-with-badge__issue-badge" />}
        </i>
      </button>
    );
  }

  const hasIcon = icon && iconComponent;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const hasTitle = (hasIcon || backButton) && title;
  const columnIndex = useColumnIndexContext();

  const titleContents = (
    <>
      {!backButton && hasIcon && (
        <Icon id={icon} icon={iconComponent} className="column-header__icon" />
      )}
      <span className="column-header__text">{title}</span>
    </>
  );

  const titleClassNames = classNames("column-header__title", {
    "column-header__title--with-back-button": !!backButton,
  });

  const component = (
    <div className={wrapperClassName}>
      <div className={headingClassName}>
        {backButton}
        {hasTitle && (
          <NavigationFocusTarget as="h1" className="column-header__title-wrapper">
            {onClick ? (
              <button
                onClick={handleTitleClick}
                className={titleClassNames}
                type="button"
                id={getColumnSkipLinkId(columnIndex)}
              >
                {titleContents}
              </button>
            ) : (
              <span className={titleClassNames} tabIndex={-1} id={getColumnSkipLinkId(columnIndex)}>
                {titleContents}
              </span>
            )}
          </NavigationFocusTarget>
        )}

        <div className="column-header__buttons">
          {extraButton}
          {collapseButton}
        </div>
      </div>

      <div
        className={collapsibleClassName}
        tabIndex={collapsed ? -1 : undefined}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className="column-header__collapsible-inner">
          {(!collapsed || animating) && collapsedContent}
        </div>
      </div>

      {appendContent}
    </div>
  );

  if (placeholder) {
    return component;
  } else {
    return <ButtonInTabsBar>{component}</ButtonInTabsBar>;
  }
};

// eslint-disable-next-line import/no-default-export
export default ColumnHeader;
